import React, {
  useCallback, useEffect, useState,
} from 'react';
import AdminContainer from 'components/Admin/AdminContainer';
import { DocRequestEntity, DocRequestFilters } from 'types/DocRequest';
import handlePromise from 'utils/Promise';
import DocsRequestService from 'services/api/DocsRequest';
import InvestmentAccountService from 'services/api/InvestmentAccount';
import DocsView from './components/View';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface DocRequestApiResult {
  data: DocRequestEntity[];
  totalPages: number;
  page: number;
  maxPerPage: number;
  orderBy: string | null;
  totalItems: number;
}

const INITIAL_PAGE_SIZE = 20;

type SortDirection = 'Ascending' | 'Descending' | '';
interface SortState {
  orderBy: string;
  sortDirection: SortDirection;
}

type DashboardFilterInput = {
  selectedEstados?: string[];
  selectedCategorias?: string[];
  selectedCuentas?: number[];
  searchNombre?: string;
  searchDescripcion?: string;
  searchId?: string;
  searchCuentaName?: string;
};

type QuickRangeValue =
  | 'overdue'
  | 'today'
  | 'next7days'
  | 'next30days'
  | 'currentMonth'
  | 'currentYear';

type InvestmentAccountOption = {
  id: number;
  name: string;
};

const DEFAULT_SORT_STATE: SortState = {
  orderBy: 'createdAt',
  sortDirection: 'Descending',
};

function buildSolicitudDocFilter( input: DashboardFilterInput = {} ) {
  const stateParts = ( input.selectedEstados ?? [] )
    .filter( ( estado ) => estado?.trim() )
    .map( ( estado ) => `estado##${estado.trim()}` );
  const categoryParts = ( input.selectedCategorias ?? [] )
    .filter( ( categoria ) => categoria?.trim() )
    .map( ( categoria ) => `categoria##${categoria.trim()}` );
  const accountParts = ( input.selectedCuentas ?? [] )
    .filter( ( accountId ) => !!accountId )
    .map( ( accountId ) => `cuentaDeInversionID##${accountId}` );

  const parts: string[] = [...stateParts, ...categoryParts, ...accountParts];
  if ( input.searchNombre?.trim() ) {
    parts.push( `nombre##${input.searchNombre.trim()}` );
  }
  if ( input.searchDescripcion?.trim() ) {
    parts.push( `descripcion##${input.searchDescripcion.trim()}` );
  }
  if ( input.searchId?.trim() ) {
    parts.push( `id##${input.searchId.trim()}` );
  }
  if ( input.searchCuentaName?.trim() ) {
    parts.push( `cuentaDeInversionName##${input.searchCuentaName.trim()}` );
  }

  return parts.join( '@@' );
}

function formatDateOnly( date: Date ) {
  const year = date.getFullYear();
  const month = String( date.getMonth() + 1 ).padStart( 2, '0' );
  const day = String( date.getDate() ).padStart( 2, '0' );
  return `${year}-${month}-${day}`;
}

function addDays( baseDate: Date, days: number ) {
  const nextDate = new Date( baseDate );
  nextDate.setDate( nextDate.getDate() + days );
  return nextDate;
}

function getQuickDateRange( quickRange?: string | null ) {
  if ( !quickRange ) return {};

  const today = new Date();
  const normalizedToday = new Date( today.getFullYear(), today.getMonth(), today.getDate() );
  const range = quickRange as QuickRangeValue;

  if ( range === 'overdue' ) {
    return {
      to: formatDateOnly( addDays( normalizedToday, -1 ) ),
    };
  }

  if ( range === 'today' ) {
    const date = formatDateOnly( normalizedToday );
    return { from: date, to: date };
  }

  if ( range === 'next7days' ) {
    return {
      from: formatDateOnly( addDays( normalizedToday, -7 ) ),
      to: formatDateOnly( addDays( normalizedToday, 7 ) ),
    };
  }

  if ( range === 'next30days' ) {
    return {
      from: formatDateOnly( addDays( normalizedToday, -30 ) ),
      to: formatDateOnly( addDays( normalizedToday, 30 ) ),
    };
  }

  if ( range === 'currentMonth' ) {
    const firstDay = new Date( normalizedToday.getFullYear(), normalizedToday.getMonth(), 1 );
    const lastDay = new Date( normalizedToday.getFullYear(), normalizedToday.getMonth() + 1, 0 );
    return {
      from: formatDateOnly( firstDay ),
      to: formatDateOnly( lastDay ),
    };
  }

  if ( range === 'currentYear' ) {
    const firstDay = new Date( normalizedToday.getFullYear(), 0, 1 );
    const lastDay = new Date( normalizedToday.getFullYear(), 11, 31 );
    return {
      from: formatDateOnly( firstDay ),
      to: formatDateOnly( lastDay ),
    };
  }

  return {};
}

function normalizeFilterToArray<T>( value?: T | T[] | null ) {
  if ( Array.isArray( value ) ) return value;
  if ( value ) return [value];
  return [];
}

function DocsDashboardScreen() {
  const [loading, setLoading] = useState( false );
  const [data, setData] = useState<DocRequestEntity[]>( [] );
  const [currentPage, setCurrentPage] = useState( 1 );
  const [pageSize, setPageSize] = useState( INITIAL_PAGE_SIZE );
  const [totalPages, setTotalPages] = useState( 1 );

  const [currentFilters, setCurrentFilters] = useState<DocRequestFilters | undefined>( undefined );

  const [sortState, setSortState] = useState<SortState>( DEFAULT_SORT_STATE );
  const [investmentAccounts, setInvestmentAccounts] = useState<InvestmentAccountOption[]>( [] );
  const [isLoadingInvestmentAccounts, setIsLoadingInvestmentAccounts] = useState( false );

  const loadInvestmentAccounts = useCallback( async () => {
    setIsLoadingInvestmentAccounts( true );
    const [, response, accountsData] = await handlePromise(
      InvestmentAccountService.getAllBackOffice( { Page: 1, ItemsPerPage: 999 } ),
    );
    setIsLoadingInvestmentAccounts( false );

    if ( response.ok && accountsData?.data ) {
      setInvestmentAccounts(
        ( accountsData.data as any[] ).map( ( account ) => ( {
          id: Number( account.cuentaDeInversionId ),
          name: account.nameCuentadeInversion,
        } ) ).filter( ( account ) => account.id && account.name ),
      );
    }
  }, [] );

  const fetchData = useCallback( async (
    filters: DocRequestFilters | undefined = currentFilters,
    page: number = currentPage,
    itemsPerPage: number = pageSize,
    sort: SortState = sortState,
  ) => {
    setLoading( true );
    setCurrentFilters( filters );
    setSortState( sort );

    const selectedEstados = normalizeFilterToArray( filters?.estado );
    const selectedCategorias = normalizeFilterToArray( filters?.categoria );
    const selectedCuentas = normalizeFilterToArray( filters?.cuentaDeInversionID );

    const filter = buildSolicitudDocFilter( {
      selectedEstados,
      selectedCategorias,
      selectedCuentas,
      searchNombre: filters?.nombre,
      searchDescripcion: filters?.descripcion,
      searchId: filters?.id,
      searchCuentaName: filters?.cuentaDeInversionName,
    } );

    const fechaLimiteRange = getQuickDateRange( filters?.fechaLimiteQuickRange );
    const fechaExpiracionRange = getQuickDateRange( filters?.fechaExpiracionQuickRange );

    const [, response, responseData] = await handlePromise(
      DocsRequestService.getAllDocRequests( {
        Filter: filter,
        OrderBy: sort.sortDirection === '' ? DEFAULT_SORT_STATE.orderBy : sort.orderBy,
        SortDirection: sort.sortDirection === '' ? DEFAULT_SORT_STATE.sortDirection : sort.sortDirection,
        ItemsPerPage: itemsPerPage,
        Page: page,
        ...( fechaLimiteRange.from ? { fechaLimiteFrom: fechaLimiteRange.from } : {} ),
        ...( fechaLimiteRange.to ? { fechaLimiteTo: fechaLimiteRange.to } : {} ),
        ...( fechaExpiracionRange.from ? { fechaExpiracionFrom: fechaExpiracionRange.from } : {} ),
        ...( fechaExpiracionRange.to ? { fechaExpiracionTo: fechaExpiracionRange.to } : {} ),
      } ),
    );
    setLoading( false );

    if ( response.ok && responseData ) {
      setData( responseData.data );
      setTotalPages( responseData.totalPages );
      setCurrentPage( responseData.page );
    }
  }, [currentFilters, currentPage, pageSize, sortState] );

  const onPageChange = useCallback( ( page: number ) => {
    if ( page === currentPage ) return;
    fetchData( currentFilters, page, pageSize, sortState );
  }, [fetchData, currentFilters, pageSize, currentPage, sortState] );

  const onPageSizeChange = useCallback( ( newSize: number ) => {
    setPageSize( newSize );
    fetchData( currentFilters, 1, newSize, sortState );
  }, [fetchData, currentFilters, sortState] );

  // INICIO AJUSTE: Lógica de ordenación de tres clics (Desc -> Asc -> Desactivar)
  const onSortChange = useCallback( ( newOrderBy: string ) => {
    let nextSortDirection: SortDirection = 'Descending';
    let nextOrderBy = newOrderBy;

    if ( newOrderBy === sortState.orderBy ) {
      // Si la columna es la misma, ciclo: Descending -> Ascending -> Desactivar
      if ( sortState.sortDirection === 'Descending' ) {
        nextSortDirection = 'Ascending'; // Click 2
      } else if ( sortState.sortDirection === 'Ascending' ) {
        nextSortDirection = ''; // Click 3: Desactivar ordenación
        nextOrderBy = DEFAULT_SORT_STATE.orderBy; // Usaremos el campo por defecto para el fetch
      } else {
        // Esto no debería pasar con el estado inicial, pero lo dejamos como seguro
        nextSortDirection = 'Descending';
      }
    } else {
      // Nueva columna: Ordena por Descending (Click 1)
      nextSortDirection = 'Descending';
    }

    const newSortState: SortState = {
      orderBy: nextOrderBy,
      sortDirection: nextSortDirection,
    };

    fetchData( currentFilters, 1, pageSize, newSortState );
  }, [fetchData, currentFilters, pageSize, sortState] );
  // FIN AJUSTE

  useEffect( () => {
    fetchData( undefined, 1, INITIAL_PAGE_SIZE, DEFAULT_SORT_STATE );
    loadInvestmentAccounts();
  }, [] );

  const handleFilterChange = useCallback( ( filters: DocRequestFilters ) => {
    fetchData( filters, 1, pageSize, sortState );
  }, [fetchData, pageSize, sortState] );

  const updateItem = useCallback( ( newItem: DocRequestEntity ) => {
    if ( newItem ) {
      setData( ( prev ) => {
        const i = prev?.findIndex( ( item ) => item.id === newItem.id );

        if ( i === -1 || !prev ) return prev;

        const newData = JSON.parse( JSON.stringify( prev ) ) as DocRequestEntity[];
        newData[i as number] = newItem;
        return newData;
      } );
    }
  }, [] );
  return (
    <AdminContainer>
      <DocsView
        data={data}
        fetchData={handleFilterChange}
        setItem={updateItem}
        loading={loading}
        investmentAccounts={investmentAccounts}
        isLoadingInvestmentAccounts={isLoadingInvestmentAccounts}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        currentSort={sortState}
        onSortChange={onSortChange}
      />
    </AdminContainer>
  );
}

export default DocsDashboardScreen;
